import { useEffect, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import { Button, Snackbar, Alert, Dialog } from "@mui/material";
import { List, Map } from "react-feather";
import axios from "axios";
import Loader from "../../../components/Loader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import Step1 from "./steps/Step1";
import Step2 from "./steps/Step2";
import Step3 from "./steps/Step3";
import ListView from "./ListView";
import PageHeader from "../../../components/PageHeader";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import BookingPaymentPopup from "../../../components/popups/BookingPaymentPopup";
import AssignDeliveryDriverPopup from "../../../components/popups/AssignDeliveryDriverPopup";
import UpdateNotePopup from "../../../components/popups/UpdateNotePopup";
import BookingSkeleton from "./BookingSkeleton";

const BookingList = () => {
    const auth = authData();
    const API_TOKEN = auth?.api_token;
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filterVars, setFilterVars] = useState({ searchKey: "", date: null });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const [formOpen, setFormOpen] = useState(false);
    const [driverList, setDriverList] = useState([]);
    const [selectedDriverList, setSelectedDriverList] = useState([]);
    const [selected, setSelected] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [routes, setRoutes] = useState([]);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [selectBooking, setSelectBooking] = useState(null);
    const [noteData, setNoteData] = useState(null);
    const [viewType, setViewType] = useState('map');

    const { websiteData } = useWebsite();
    useSiteBranding("Assign Driver", websiteData);

    useEffect(() => {
        const fetchBookings = () => {
            setContentLoading(true);
            axios.get(process.env.REACT_APP_API_URL + "v1/delivery/assign-bookings", {
                headers: {
                    Authorization: `Bearer ${API_TOKEN}`,
                }
            }).then((response) => {
                setContentLoading(false);
                setAllData(response.data.data);
            }).catch((error) => {
                setContentLoading(false);
            });
        };

        fetchBookings();
    }, [API_TOKEN]);

    useEffect(() => {
        let filterredTempData = allData;

        if (filterVars?.searchKey !== "") {
            let inputVal = filterVars?.searchKey;
            inputVal = inputVal.toLowerCase();
            filterredTempData = filterredTempData.filter((item) => {
                return (item.booking_id.toLowerCase().includes(inputVal) || item.post_code.toLowerCase().includes(inputVal) || item.customer_name.toLowerCase().includes(inputVal) || item.pickup_details?.address.toLowerCase().includes(inputVal) || item.pickup_details?.phone.toLowerCase().includes(inputVal));
            });
        }

        if (filterVars?.date) {
            filterredTempData = filterredTempData.filter((i) => dayjs(i?.pickup_date).isBetween(dayjs(filterVars?.date[0]).format("YYYY-MM-DD"), dayjs(filterVars?.date[1]).format("YYYY-MM-DD"), "day", "[]"));
        }

        setFilteredData(filterredTempData);
    }, [allData, filterVars]);

    const selectRowEvent = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    }

    useEffect(() => {
        axios.get(process.env.REACT_APP_API_URL + "v1/delivery/d-driver-dd-list", {
            headers: {
                Authorization: `Bearer ${API_TOKEN}`,
            }
        }).then((response) => {
            if (response.data.status === "SUCCESS") {
                setDriverList(response.data.data);
            }
        });
    }, [API_TOKEN]);

    const getOptimizeRoute = () => {
        setLoading(true);

        let bookingListTemp = allData?.filter(i => selected?.indexOf(i?.id) > -1)?.map(i => {
            return { id: i?.id, booking_id: i?.booking_id, customer_name: i?.customer_name, address: i?.recipient_details?.address, lat: i?.recipient_details?.latitude, lng: i?.recipient_details?.longitude };
        });
        let driverListTemp = driverList?.filter(i => selectedDriverList?.indexOf(i?.id) > -1)?.map(i => {
            return { id: i?.id, name: i?.name, address: i?.address, lat: i?.latitude, lng: i?.longitude };
        });

        setLoading(true);

        axios.post(process.env.REACT_APP_API_URL + 'v1/delivery/optimize-route', { drivers: driverListTemp, dropoffs: bookingListTemp }, {
            headers: {
                Authorization: `Bearer ${auth?.api_token}`,
            }
        }).then(response => {
            setLoading(false);
            setRoutes(response.data.data);
            setCurrentStep(2);
        }).catch(error => {
            setLoading(false);
        });
    }

    const paymentItem = (response) => {
        setLoading(false);
        setPaymentModalOpen(false);
        setSnackbarOpen(true);
        setSnackbarMessage(response.message);
        setSnackbarType("success");

        setAllData((prev) => {
            return prev.map((i) => {
                if (i?.id === response.data.id) {
                    return { ...i, payment_status: response.data.payment_status };
                }
                return i;
            });
        });

        setSelectBooking(null);
    }

    return (<>
        <Loader open={loading} setOpen={setLoading.bind(this)} />

        <div className={`${styles.MainDiv}`}>

            <PageHeader title="Delivery" trail={[{ label: 'Assign Driver' }]} />

            <div className={`${styles.MainCard}`}>
                {contentLoading && <BookingSkeleton />}

                {!contentLoading && <div className={`${styles.ViewSwitchWrap}`} role="group" aria-label="Choose view">
                    <Button
                        aria-label="Map view"
                        aria-pressed={viewType === 'map'}
                        className={viewType === 'map' ? `${styles.SwitchBtn} ${styles.SwitchBtnActive}` : `${styles.SwitchBtn}`}
                        onClick={(e) => setViewType('map')}
                    >
                        <Map size={16} /> Map
                    </Button>
                    <Button
                        aria-label="List view"
                        aria-pressed={viewType === 'list'}
                        className={viewType === 'list' ? `${styles.SwitchBtn} ${styles.SwitchBtnActive}` : `${styles.SwitchBtn}`}
                        onClick={(e) => setViewType('list')}
                    >
                        <List size={16} /> List
                    </Button>
                </div>}

                {!contentLoading && (<>
                    {viewType === 'map' && (<>
                        {currentStep === 1 && <Step1 selected={selected} filteredData={filteredData} filterVars={filterVars} driverList={driverList} selectedDriverList={selectedDriverList} setFilterVars={setFilterVars.bind(this)} setFormOpen={setFormOpen.bind(this)} setSelectedDriverList={setSelectedDriverList.bind(this)} getOptimizeRoute={getOptimizeRoute.bind(this)} selectRowEvent={selectRowEvent.bind(this)} setSelected={setSelected.bind(this)} setPaymentModalOpen={setPaymentModalOpen.bind(this)} setSelectBooking={setSelectBooking.bind(this)} setNoteModalOpen={setNoteModalOpen.bind(this)} setNoteData={setNoteData.bind(this)} />}

                        {currentStep === 2 && <Step2 routes={routes} setCurrentStep={setCurrentStep.bind(this)} />}

                        {currentStep === 3 && <Step3 routes={routes} setCurrentStep={setCurrentStep.bind(this)} setLoading={setLoading.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setRoutes={setRoutes.bind(this)} />}
                    </>)}

                    {viewType === 'list' && <>
                        <ListView filteredData={filteredData} setPaymentModalOpen={setPaymentModalOpen.bind(this)} filterVars={filterVars} setFilterVars={setFilterVars.bind(this)} selected={selected} setSelectBooking={setSelectBooking.bind(this)} setNoteData={setNoteData.bind(this)} setNoteModalOpen={setNoteModalOpen.bind(this)} selectRowEvent={selectRowEvent.bind(this)} setSelected={setSelected.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setAllData={setFilteredData.bind(this)}
                            driverList={driverList} />
                    </>}
                </>)}
            </div>
        </div>

        <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
            <AssignDeliveryDriverPopup setFormOpen={setFormOpen.bind(this)} selectedDriverList={selectedDriverList} setSelectedDriverList={setSelectedDriverList.bind(this)} setLoading={setLoading.bind(this)} driverList={driverList} />
        </Dialog>

        <Dialog open={paymentModalOpen} onClose={(e) => setPaymentModalOpen(false)} fullWidth maxWidth="sm">
            <BookingPaymentPopup modalClose={setPaymentModalOpen.bind(this)} paymentItem={paymentItem.bind(this)} selectBooking={selectBooking} setLoading={setLoading.bind(this)} />
        </Dialog>

        <Dialog fullWidth maxWidth="sm" open={noteModalOpen} onClose={(e) => setNoteModalOpen(false)}>
            <UpdateNotePopup setNoteModalOpen={setNoteModalOpen.bind(this)} noteData={noteData} selectBooking={selectBooking} setLoading={setLoading.bind(this)} setAllData={setAllData.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} />
        </Dialog >

        <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={(e) => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <Alert
                onClose={(e) => setSnackbarOpen(false)}
                severity={snackbarType}
                variant="filled"
                sx={{ width: "100%" }}
            >
                {snackbarMessage}
            </Alert>
        </Snackbar>
    </>);
};

export default withAdminAuth(BookingList);